import { ref, computed, toValue } from 'vue'
import { defineStore } from 'pinia'
import { generateStoreName } from '@/services/store-utils'

export const useAuthStore = defineStore(
  generateStoreName('auth'),
  () => {
    const user = ref(null)
    const token = ref('')
    const authStatus = ref('ANONYMOUS')
    const permissions = ref([])
    const rabbitCreds = ref(null)
    const credentials = ref({
      username: null,
      password: null,
      remembered: false,
      autoLogin: true,
    })
    const isLoggedIn = computed(() => toValue(authStatus) === 'SUCCESS')
    const userPermissions = computed(() => toValue(permissions))
    const webPermissions = computed(() =>
      toValue(permissions)
        ?.filter((p) => p.platform === 'WEB' && p.context === 'URI' && p.type !== 'NAVIGATE')
        ?.reduce((acc, curr) => {
          curr.granted = true
          if (acc) {
            if (!acc[curr.value]) acc[curr.value] = {}
            acc[curr.value][curr.type] = curr
          }
          return acc
        }, {})
    )

    const menuPermissions = computed(() =>
      toValue(permissions)
        ?.filter((p) => p.platform === 'WEB' && p.context === 'URI' && p.type === 'NAVIGATE')
        ?.reduce((acc, curr) => {
          curr.granted = true
          if (acc) {
            acc[curr.value] = curr
          }
          return acc
        }, {})
    )

    const isTypeA2OrBoth = computed(() => {
      const type = toValue(user)?.type?.toUpperCase()
      return type === 'A2' || type === 'BOTH'
    })

    const roles = computed(() => toValue(user)?.roles || [])

    const isTechnician = computed(() => {
      const profile = toValue(user)
      const name = profile ? profile.roleIdentifier : null
      return name === 'ROLE_TECHNICIAN'
    })
    const isTechOrAdmin = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_TECHNICIAN' || name === 'ROLE_ADMIN'
    })
    const isAdmin = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_ADMIN'
    })
    const isStandard = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_STANDARD'
    })
    const isSupervisor = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_SUPERVISOR'
    })
    const isCustomer = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_CUSTOMER'
    })
    const isSupport = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_SUPPORT'
    })
    const isStakeHolder = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_STAKEHOLDER'
    })
    const isCaregiver = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_CAREGIVER'
    })
    const isSearchAdmin = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_SEARCH_ADMIN'
    })
    const isSearchUser = computed(() => {
      const profile = toValue(user)
      const name = profile?.roleIdentifier || null
      return name === 'ROLE_SEARCH_USER'
    })
    const username = computed(() => toValue(user)?.username)
    const isFacilityStakeHolder = computed(() => toValue(user)?.roleIdentifier === 'ROLE_FACILITY_STAKEHOLDER')
    const isEnterpriseStakeHolder = computed(() => toValue(user)?.roleName === 'ROLE_ENTERPRISE_STAKEHOLDER')
    const isShowcaseAccount = computed(() => {
      const uname = toValue(username)
      return uname === 'HAROLD' || uname === 'HAROLDC'
    })
    const updateCredentials = ({ username, password, remembered, autoLogin }) => {
      credentials.value = { username, password, remembered, autoLogin }
    }
    const login = ({ user: u, token: t, permissions: perms, success, brokerCreds }) => {
      authStatus.value = success ? 'SUCCESS' : 'FAILURE'
      token.value = t || ''
      user.value = u ?? null
      rabbitCreds.value = brokerCreds
      permissions.value = perms || []
    }
    function logout() {
      user.value = null
      permissions.value = []
      token.value = ''
      rabbitCreds.value = null
      authStatus.value = 'ANONYMOUS'
    }
    const pagePermissions = (uri) => toValue(webPermissions)[uri] || {}
    const canDo = (uri, action) => {
      const permissions = toValue(pagePermissions(uri))
      return permissions[action]?.granted ?? false
    }
    const canSee = (uri) => {
      const permissions = toValue(menuPermissions)[uri.startsWith('/') ? uri.substring(1) : uri] || null
      return permissions?.granted ?? false
    }
    return {
      credentials,
      user,
      token,
      authStatus,
      permissions,
      pagePermissions,
      isShowcaseAccount,
      isCaregiver,
      isStakeHolder,
      isSupport,
      isCustomer,
      isSupervisor,
      isStandard,
      isTechnician,
      isTechOrAdmin,
      roles,
      isAdmin,
      isTypeA2OrBoth,
      isEnterpriseStakeHolder,
      isFacilityStakeHolder,
      username,
      isSearchUser,
      isSearchAdmin,
      login,
      logout,
      canSee,
      canDo,
      updateCredentials,
      isLoggedIn,
      userPermissions,
      menuPermissions,
      webPermissions,
      rabbitCreds,
    }
  },
  { persist: true }
)
